//* Libraries imports
import { useState } from "react";
import { TextQuoteIcon, MicIcon, VideoIcon, FileIcon } from "lucide-react";

//* Componentes imports
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { MediaUpload } from "./components/MediaUpload";

export type TutorialData = {
  text?: { link?: string; name?: string };
  audio?: { link?: string; name?: string };
  video?: { link?: string; name?: string };
  pdf?: { link?: string; name?: string };
};

type TutorialSectionProps = {
  tutorial?: TutorialData;
  onChange: (tutorial: TutorialData) => void;
  onUpload: (file: File) => Promise<{ data: { path: string } }>;
  loadingFile?: boolean;
  id?: string;
};

export function TutorialSection({
  tutorial = {},
  onChange,
  onUpload,
  loadingFile = false,
  id = "tutorial",
}: TutorialSectionProps) {
  const [activeType, setActiveType] = useState<
    "text" | "audio" | "video" | "pdf"
  >("text");

  const handleUpdate = (
    type: "text" | "audio" | "video" | "pdf",
    field: "link" | "name",
    value: string,
  ) => {
    const currentTypeData = tutorial[type] || { link: "", name: "" };

    onChange({
      ...tutorial,
      [type]: {
        ...currentTypeData,
        [field]: value,
      },
    });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <label className="text-sm font-semibold text-gray-700">Tutorial</label>

      {/* Type Selection Buttons */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Button
          variant={"outline"}
          disabled={loadingFile}
          className={
            "flex items-center justify-center gap-2 rounded-xl p-2 hover:bg-blue-50 hover:border-blue-500 transition-colors " +
            (activeType === "text" ? "border-blue-500 " : "")
          }
          onClick={() => setActiveType("text")}
        >
          <TextQuoteIcon
            className={
              "size-5 " + (activeType === "text" ? "text-blue-500 " : "")
            }
          />
          <span
            className={
              "text-xs " + (activeType === "text" ? "text-blue-500 " : "")
            }
          >
            Texto
          </span>
        </Button>

        <Button
          variant={"outline"}
          disabled={loadingFile}
          className={
            "flex items-center justify-center gap-2 rounded-xl p-2 hover:bg-blue-50 hover:border-blue-500 transition-colors " +
            (activeType === "audio" ? "border-blue-500 " : "")
          }
          onClick={() => setActiveType("audio")}
        >
          <MicIcon
            className={
              "size-5 " + (activeType === "audio" ? "text-blue-500 " : "")
            }
          />
          <span
            className={
              "text-xs " + (activeType === "audio" ? "text-blue-500 " : "")
            }
          >
            Áudio
          </span>
        </Button>

        <Button
          variant={"outline"}
          disabled={loadingFile}
          className={
            "flex items-center justify-center gap-2 rounded-xl p-2 hover:bg-blue-50 hover:border-blue-500 transition-colors " +
            (activeType === "video" ? "border-blue-500 " : "")
          }
          onClick={() => setActiveType("video")}
        >
          <VideoIcon
            className={
              "size-5 " + (activeType === "video" ? "text-blue-500 " : "")
            }
          />
          <span
            className={
              "text-xs " + (activeType === "video" ? "text-blue-500 " : "")
            }
          >
            Vídeo
          </span>
        </Button>

        <Button
          variant={"outline"}
          disabled={loadingFile}
          className={
            "flex items-center justify-center gap-2 rounded-xl p-2 hover:bg-blue-50 hover:border-blue-500 transition-colors " +
            (activeType === "pdf" ? "border-blue-500 " : "")
          }
          onClick={() => setActiveType("pdf")}
        >
          <FileIcon
            className={
              "size-5 " + (activeType === "pdf" ? "text-blue-500 " : "")
            }
          />
          <span
            className={
              "text-xs " + (activeType === "pdf" ? "text-blue-500 " : "")
            }
          >
            PDF
          </span>
        </Button>
      </div>

      {/* Content Area */}
      <div className="w-full relative">
        {activeType === "text" && (
          <Textarea
            className="h-12 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
            placeholder={"Escreva o texto aqui"}
            value={tutorial.text?.link || ""}
            onChange={(e) => handleUpdate("text", "link", e.target.value)}
          />
        )}

        {activeType === "audio" && (
          <MediaUpload
            id={id}
            type="audio"
            data={{
              link: tutorial.audio?.link || "",
              name: tutorial.audio?.name || "",
            }}
            onUpdate={(field, value) => handleUpdate("audio", field, value)}
            onUpload={onUpload}
            loadingFile={loadingFile}
          />
        )}

        {activeType === "video" && (
          <MediaUpload
            id={id}
            type="video"
            data={{
              link: tutorial.video?.link || "",
              name: tutorial.video?.name || "",
            }}
            onUpdate={(field, value) => handleUpdate("video", field, value)}
            onUpload={onUpload}
            loadingFile={loadingFile}
          />
        )}

        {activeType === "pdf" && (
          <MediaUpload
            id={id}
            type="pdf"
            data={{
              link: tutorial.pdf?.link || "",
              name: tutorial.pdf?.name || "",
            }}
            onUpdate={(field, value) => handleUpdate("pdf", field, value)}
            onUpload={onUpload}
            loadingFile={loadingFile}
          />
        )}
      </div>
    </div>
  );
}
